'use client'

import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useApiRequest } from '@/hooks/use-api'
import { useOptionalCurrentBrand } from '@/hooks/use-current-brand'
import {
  buildShareCreatePayload,
  buildShareListQuery,
} from '@/lib/share-create-payload'
import {
  CheckIcon,
  CopyIcon,
  KeyIcon,
  Loader2Icon,
  LockIcon,
  PencilIcon,
  ShieldAlertIcon,
  Trash2Icon,
} from 'lucide-react'
import { useFormatter, useTranslations } from 'next-intl'
import { toast } from 'sonner'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { PasswordInput } from './password-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { parseServerDate } from '@/lib/server-date'

type ResourceType = 'chat_attachment' | 'conversation' | 'remix_project'

type ShareLinkRow = {
  id: string
  token: string | null
  resource_type: ResourceType
  resource_id: string
  created_at: string
  revoked_at: string | null
  expires_at: string | null
  public_url: string
  has_password: boolean
}

type ExpiryOption = '0' | '1h' | '24h' | '7d' | '30d'

const EXPIRY_DURATION_MS: Record<ExpiryOption, number> = {
  '0': 0,
  '1h': 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
}

export interface ShareDialogProps {
  resourceType: ResourceType
  resourceId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  // Required when resourceType === 'conversation'. The list of message ids
  // to freeze into the snapshot at create time (≤500). Ignored for other
  // kinds — server-side schema also rejects passing them where they don't
  // belong, so a mistaken pass-through fails fast at POST instead of silently
  // shaping a wider share.
  snapshotMessageIds?: string[]
  /**
   * A second kind of link over the same resource, rendered below this one.
   *
   * A slot rather than a built-in section: this dialog manages `share_links`,
   * which are anonymous read-only views, and the canvas's live links are a
   * different table with different guarantees. Passing the section in keeps
   * that separation in the code while still putting both behind the one button
   * a person reaches for when they want to hand their work to someone — which
   * is the only place the second kind would ever be found.
   */
  secondaryLinkSection?: React.ReactNode
}

type ListState =
  | { status: 'loading_list' }
  | { status: 'list_loaded'; rows: ShareLinkRow[] }
  | { status: 'list_error'; message: string }

export function ShareDialog({
  resourceType,
  resourceId,
  open,
  onOpenChange,
  snapshotMessageIds,
  secondaryLinkSection,
}: ShareDialogProps) {
  const t = useTranslations('Share.dialog')
  const tCommon = useTranslations('Common')
  const { request } = useApiRequest()
  const currentBrand = useOptionalCurrentBrand()
  const [state, setState] = useState<ListState>({ status: 'loading_list' })
  const [creating, setCreating] = useState(false)
  const [expiry, setExpiry] = useState<ExpiryOption>('0')
  const [createPassword, setCreatePassword] = useState('')
  const [newlyCreated, setNewlyCreated] = useState<ShareLinkRow | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  // Two-step revoke: click Revoke on a row -> AlertDialog confirms.
  // Holding the row (not just an id) keeps the dialog stable even if the
  // list refreshes during the confirm window.
  const [confirmingRow, setConfirmingRow] = useState<ShareLinkRow | null>(null)
  // Edit-password sub-dialog: ``null`` closed; row carries the link being edited.
  const [editingPasswordRow, setEditingPasswordRow] =
    useState<ShareLinkRow | null>(null)

  const expiryOptions = useMemo<{ value: ExpiryOption; label: string }[]>(
    () => [
      { value: '0', label: t('expirationNever') },
      { value: '1h', label: t('expiration1h') },
      { value: '24h', label: t('expiration24h') },
      { value: '7d', label: t('expiration7d') },
      { value: '30d', label: t('expiration30d') },
    ],
    [t],
  )
  const isArtifact = resourceType === 'chat_attachment'
  const isConversation = resourceType === 'conversation'
  const isCanvas = resourceType === 'remix_project'

  const refresh = useCallback(async () => {
    setState({ status: 'loading_list' })
    try {
      const rows = await request<ShareLinkRow[]>({
        url: '/api/shares',
        query: buildShareListQuery({
          resourceType,
          resourceId,
          brandId: currentBrand?.brandId,
        }),
      })
      setState({ status: 'list_loaded', rows })
    } catch (err) {
      console.error('[share-list]', err)
      setState({
        status: 'list_error',
        message: t('errorLoadList'),
      })
    }
  }, [currentBrand?.brandId, request, resourceType, resourceId, t])

  useEffect(() => {
    if (open) {
      setNewlyCreated(null)
      setCreatePassword('')
      refresh()
    }
  }, [open, refresh])

  const onCreate = useCallback(async () => {
    if (isConversation && (!snapshotMessageIds || snapshotMessageIds.length === 0)) {
      toast.error(t('errorEmptyConversation'))
      return
    }
    setCreating(true)
    try {
      const body = buildShareCreatePayload({
        resourceType,
        resourceId,
        brandId: isArtifact || isCanvas ? currentBrand?.brandId : undefined,
        expiresAt:
          expiry === '0'
            ? undefined
            : new Date(Date.now() + EXPIRY_DURATION_MS[expiry]).toISOString(),
        // Length is validated server-side (4..128); the dialog forwards the
        // raw value and surfaces a 422 as the standard create-failed toast.
        password: createPassword || undefined,
        snapshotMessageIds: isConversation ? snapshotMessageIds : undefined,
      })
      const row = await request<ShareLinkRow>({
        url: '/api/shares',
        method: 'POST',
        body,
      })
      setNewlyCreated(row)
      setCreatePassword('')
      // Refresh list so subsequent revokes target real rows.
      await refresh()
      toast.success(t('toastCreatedSuccess'))
    } catch (err) {
      console.error('share link create failed', err)
      toast.error(t('toastCreateFailed'))
    } finally {
      setCreating(false)
    }
  }, [
    createPassword,
    currentBrand?.brandId,
    expiry,
    isArtifact,
    isCanvas,
    isConversation,
    refresh,
    request,
    resourceId,
    resourceType,
    snapshotMessageIds,
    t,
  ])

  const onSetPassword = useCallback(
    async (row: ShareLinkRow, password: string | null) => {
      try {
        await request({
          url: `/api/shares/${row.id}/password`,
          method: 'POST',
          body: { password },
        })
        toast.success(
          password === null
            ? t('toastPasswordClearedSuccess')
            : t('toastPasswordSetSuccess'),
        )
        await refresh()
      } catch (err) {
        console.error('share link password update failed', err)
        toast.error(t('toastPasswordFailed'))
      }
    },
    [refresh, request, t],
  )

  const onRevoke = useCallback(
    async (row: ShareLinkRow) => {
      try {
        await request({
          url: `/api/shares/${row.id}`,
          method: 'DELETE',
        })
        if (newlyCreated && newlyCreated.id === row.id) {
          setNewlyCreated({
            ...newlyCreated,
            revoked_at: new Date().toISOString(),
          })
        }
        toast.success(t('toastRevokedSuccess'))
        await refresh()
      } catch (err) {
        console.error('share link revoke failed', err)
        toast.error(t('toastRevokeFailed'))
      }
    },
    [newlyCreated, refresh, request, t],
  )

  const onUpdateExpiry = useCallback(
    async (row: ShareLinkRow, option: ExpiryOption) => {
      const nextExpiresAt =
        option === '0'
          ? null
          : new Date(Date.now() + EXPIRY_DURATION_MS[option]).toISOString()
      try {
        await request({
          url: `/api/shares/${row.id}`,
          method: 'PATCH',
          body: { expires_at: nextExpiresAt },
        })
        toast.success(t('toastUpdatedSuccess'))
        await refresh()
      } catch (err) {
        console.error('share link expiry update failed', err)
        toast.error(t('toastUpdateFailed'))
      }
    },
    [refresh, request, t],
  )

  const onCopy = useCallback(
    async (row: ShareLinkRow) => {
      const url = row.public_url || buildPublicUrlFallback(row.token)
      if (!url) return
      try {
        await navigator.clipboard.writeText(url)
        setCopiedId(row.id)
        setTimeout(
          () => setCopiedId(cur => (cur === row.id ? null : cur)),
          1500,
        )
        toast.success(t('toastCopied'))
      } catch {
        toast.error(t('toastCopyFailed'))
      }
    },
    [t],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* ``w-full`` overrides the shared DialogContent default of ``w-fit``,
          which otherwise stretches the dialog to the natural width of an
          unwrapped share URL inside the rows below (only ``truncate`` is
          declared on the inner ``<code>``; the parent dialog needs an
          explicit width cap or it grows to ``max-w-[80vw]``). Keep the
          ``sm:max-w-lg`` cap so wide viewports stay readable. */}
      <DialogContent className="w-full sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isConversation
              ? t('titleConversation')
              : isCanvas
                ? t('titleCanvas')
                : isArtifact
                  ? t('titleArtifact')
                  : t('titleDeliverable')}
          </DialogTitle>
          <DialogDescription>
            {isConversation ? t('descriptionConversation') : t('description')}
          </DialogDescription>
        </DialogHeader>

        {/* Which links this warns about stops being obvious the moment a
            second kind appears below. "Anyone with the link can view this"
            is true of a share link and UNDERSTATES a live one, which is
            editable — so when a second section is present the warning names
            the kind it is about rather than speaking for the whole dialog.
            The live section states its own terms in its own copy. */}
        <Alert>
          <ShieldAlertIcon />
          <AlertTitle>{t('warningTitle')}</AlertTitle>
          <AlertDescription>
            {secondaryLinkSection
              ? t('warningDescriptionReadOnly')
              : t('warningDescription')}
          </AlertDescription>
        </Alert>

        <div className="space-y-3">
          <div className="flex w-full min-w-0 items-end gap-2">
            <div className="min-w-0 flex-1">
              <label className="text-muted-foreground mb-1 block text-xs">
                {t('expirationLabel')}
              </label>
              <Select
                value={expiry}
                onValueChange={v => setExpiry(v as ExpiryOption)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {expiryOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={onCreate} disabled={creating}>
              {creating ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : null}
              {t('createButton')}
            </Button>
          </div>

          <div className="min-w-0">
            <label
              htmlFor="share-create-password"
              className="text-muted-foreground mb-1 block text-xs"
            >
              {t('passwordLabel')}
            </label>
            <PasswordInput
              id="share-create-password"
              autoComplete="off"
              placeholder={t('passwordPlaceholderOptional')}
              value={createPassword}
              onChange={e => setCreatePassword(e.target.value)}
              disabled={creating}
              minLength={4}
              maxLength={128}
            />
            <p className="text-muted-foreground mt-1 text-[11px]">
              {t('passwordHint')}
            </p>
          </div>
        </div>

        {newlyCreated?.token ? (
          <NewlyCreatedRow
            row={newlyCreated}
            onCopy={() => onCopy(newlyCreated)}
            copied={copiedId === newlyCreated.id}
          />
        ) : null}

        <div className="w-full min-w-0 space-y-2">
          <h3 className="text-sm font-medium">
            {secondaryLinkSection
              ? t('existingLinksHeadingReadOnly')
              : t('existingLinksHeading')}
          </h3>
          {state.status === 'loading_list' ? (
            <SkeletonRows />
          ) : state.status === 'list_error' ? (
            <p className="text-destructive text-sm">{state.message}</p>
          ) : state.rows.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {isConversation
                ? t('emptyConversation')
                : isArtifact
                  ? t('emptyArtifact')
                  : t('emptyDeliverable')}
            </p>
          ) : (
            <ul className="space-y-1.5">
              {state.rows.map(row => (
                <ExistingRow
                  key={row.id}
                  row={row}
                  copied={copiedId === row.id}
                  expiryOptions={expiryOptions}
                  onCopy={() => onCopy(row)}
                  onRevoke={() => setConfirmingRow(row)}
                  onUpdateExpiry={opt => onUpdateExpiry(row, opt)}
                  onEditPassword={() => setEditingPasswordRow(row)}
                  onClearPassword={() => onSetPassword(row, null)}
                />
              ))}
            </ul>
          )}
        </div>

        {secondaryLinkSection}
      </DialogContent>
      <EditPasswordDialog
        row={editingPasswordRow}
        onClose={() => setEditingPasswordRow(null)}
        onSubmit={async (row, password) => {
          await onSetPassword(row, password)
          setEditingPasswordRow(null)
        }}
      />
      <AlertDialog
        open={confirmingRow !== null}
        onOpenChange={open => {
          if (!open) setConfirmingRow(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirmRevokeTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('confirmRevokeDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmingRow) onRevoke(confirmingRow)
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('confirmRevokeAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  )
}

function NewlyCreatedRow({
  row,
  onCopy,
  copied,
}: {
  row: ShareLinkRow
  onCopy: () => void
  copied: boolean
}) {
  const t = useTranslations('Share.dialog')
  // ``min-w-0`` on this grid item is the critical bit: DialogContent is
  // ``display: grid`` and grid items default to ``min-width: auto`` (=
  // min-content), so the unbreakable share URL inside would otherwise
  // expand the implicit grid track and stretch the dialog past
  // ``max-w-lg``. ``min-w-0`` releases that constraint; the inner
  // ``min-w-0 flex-1 overflow-hidden`` box then bounds the truncate.
  return (
    <div className="bg-muted/40 flex w-full min-w-0 items-center gap-2 rounded-md border p-2">
      <div className="min-w-0 flex-1 overflow-hidden">
        <code className="block truncate font-mono text-xs">
          {row.public_url}
        </code>
      </div>
      <Button size="sm" variant="outline" onClick={onCopy} className="shrink-0">
        {copied ? (
          <CheckIcon className="size-4" />
        ) : (
          <CopyIcon className="size-4" />
        )}
        {copied ? t('copied') : t('copy')}
      </Button>
    </div>
  )
}

function ExistingRow({
  row,
  copied,
  expiryOptions,
  onCopy,
  onRevoke,
  onUpdateExpiry,
  onEditPassword,
  onClearPassword,
}: {
  row: ShareLinkRow
  copied: boolean
  expiryOptions: { value: ExpiryOption; label: string }[]
  onCopy: () => void
  onRevoke: () => void
  onUpdateExpiry: (option: ExpiryOption) => void
  onEditPassword: () => void
  onClearPassword: () => void
}) {
  const t = useTranslations('Share.dialog')
  const format = useFormatter()
  const isRevoked = row.revoked_at !== null
  // Snapshot "now" on first render so the impure ``Date.now()`` doesn't
  // run during render on every paint (react-hooks/purity). The list
  // refreshes when the dialog reopens / after create / after revoke,
  // which re-mounts this row and refreshes the snapshot — acceptable
  // staleness for an Expires-status hint.
  const [nowMs] = useState(() => Date.now())
  const expiresAtMs =
    row.expires_at !== null ? parseServerDate(row.expires_at).getTime() : null
  const isExpired = expiresAtMs !== null && expiresAtMs <= nowMs
  const dead = isRevoked || isExpired
  return (
    <li
      className={cn(
        'flex w-full items-center gap-2 overflow-hidden rounded-md border px-2 py-1.5 text-xs',
        dead && 'text-muted-foreground opacity-60',
      )}
    >
      <span className="min-w-0 flex-1 truncate font-mono">
        {row.token ? `…${row.token.slice(-8)}` : `…${row.id.slice(-8)}`}
      </span>
      {row.has_password ? (
        <span
          className="text-muted-foreground inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px]"
          aria-label={t('passwordBadgeLabel')}
          title={t('passwordBadgeLabel')}
        >
          <LockIcon className="size-3" />
          {t('passwordBadge')}
        </span>
      ) : null}
      <span className="text-muted-foreground shrink-0 truncate">
        {isRevoked
          ? t('statusRevoked')
          : isExpired
            ? t('statusExpired')
            : expiresAtMs !== null
              ? t('statusExpiresIn', {
                  duration: format.relativeTime(new Date(expiresAtMs), nowMs),
                })
              : t('statusNoExpiry')}
      </span>
      {!isRevoked ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="shrink-0"
              aria-label={t('editAriaLabel')}
            >
              <PencilIcon className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {expiryOptions.map(opt => (
              <DropdownMenuItem
                key={opt.value}
                onSelect={() => onUpdateExpiry(opt.value)}
              >
                {opt.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onEditPassword}>
              <KeyIcon className="size-3.5" />
              {row.has_password
                ? t('changePasswordAction')
                : t('setPasswordAction')}
            </DropdownMenuItem>
            {row.has_password ? (
              <DropdownMenuItem onSelect={onClearPassword}>
                {t('clearPasswordAction')}
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      {!dead ? (
        <>
          <Button
            size="sm"
            variant="ghost"
            onClick={onCopy}
            className="shrink-0"
            aria-label={t('copyAriaLabel')}
          >
            {copied ? (
              <CheckIcon className="size-3.5" />
            ) : (
              <CopyIcon className="size-3.5" />
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onRevoke}
            className="shrink-0"
            aria-label={t('revokeAriaLabel')}
          >
            <Trash2Icon className="size-3.5" />
          </Button>
        </>
      ) : null}
    </li>
  )
}

function EditPasswordDialog({
  row,
  onClose,
  onSubmit,
}: {
  row: ShareLinkRow | null
  onClose: () => void
  onSubmit: (row: ShareLinkRow, password: string) => Promise<void>
}) {
  const t = useTranslations('Share.dialog')
  const tCommon = useTranslations('Common')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Reset when the dialog re-opens for a different row.
  useEffect(() => {
    if (row !== null) {
      setPassword('')
      setSubmitting(false)
    }
  }, [row])

  return (
    <Dialog
      open={row !== null}
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <DialogContent className="w-full sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {row?.has_password
              ? t('changePasswordTitle')
              : t('setPasswordTitle')}
          </DialogTitle>
          <DialogDescription>{t('setPasswordDescription')}</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={async e => {
            e.preventDefault()
            if (!row || !password) return
            setSubmitting(true)
            try {
              await onSubmit(row, password)
            } finally {
              setSubmitting(false)
            }
          }}
          className="space-y-3"
        >
          <PasswordInput
            autoComplete="off"
            autoFocus
            placeholder={t('passwordPlaceholder')}
            value={password}
            onChange={e => setPassword(e.target.value)}
            minLength={4}
            maxLength={128}
            required
            disabled={submitting}
          />
          <p className="text-muted-foreground text-[11px]">
            {t('passwordHint')}
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={submitting || password.length < 4}>
              {submitting ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : null}
              {row?.has_password
                ? t('changePasswordAction')
                : t('setPasswordAction')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function SkeletonRows() {
  return (
    <div className="space-y-1.5">
      {[0, 1, 2].map(i => (
        <div key={i} className="bg-muted/40 h-7 animate-pulse rounded-md" />
      ))}
    </div>
  )
}

function buildPublicUrlFallback(token: string | null): string {
  // Best-effort fallback only — the server always returns ``public_url`` with
  // the correct path (``/share/c/<token>`` for conversation, ``/share/<token>``
  // for attachments), so this branch only runs if the API response is
  // malformed. Default to the attachment path; a copy here will 404 with the
  // standard ``not_found`` page rather than render the wrong viewer.
  if (!token) return ''
  if (typeof window === 'undefined') return ''
  return `${window.location.origin}/share/${token}`
}
